import { useCallback, useMemo } from "react";

import { useGlobalSearch } from "./useGlobalSearch";

/**
 * useGlobalSearchParallel - Composes useGlobalSearch 3 times for parallel queries.
 *
 * Runs separate queries for people, groups, and tags. Results update incrementally
 * as each query resolves. Use for Search Page where progressive loading is desired.
 *
 * @param {import("./types").GlobalSearchOptions} options
 */
export const useGlobalSearchParallel = ({
  includePeople = true,
  includeGroups = true,
  includeTags = true,
  ...options
} = {}) => {
  // Intentionally keeping all 3 updated regardless of external filters
  const people = useGlobalSearch({
    ...options,
    includePeople: true,
    includeGroups: false,
    includeTags: false,
  });

  const groups = useGlobalSearch({
    ...options,
    includePeople: false,
    includeGroups: true,
    includeTags: false,
  });

  const tags = useGlobalSearch({
    ...options,
    includePeople: false,
    includeGroups: false,
    includeTags: true,
  });

  const results = useMemo(() => {
    if (!people.results && !groups.results && !tags.results) {
      return null;
    }

    return {
      people: includePeople ? people.results?.people : null,
      teams: includeGroups ? groups.results?.teams : null,
      tags: includeTags ? tags.results?.tags : null,
      totals: {
        found:
          (includePeople ? (people.results?.people?.counters?.found ?? 0) : 0) +
          (includeGroups ? (groups.results?.teams?.counters?.found ?? 0) : 0) +
          (includeTags ? (tags.results?.tags?.counters?.found ?? 0) : 0),
        total:
          (includePeople ? (people.results?.people?.counters?.total ?? 0) : 0) +
          (includeGroups ? (groups.results?.teams?.counters?.total ?? 0) : 0) +
          (includeTags ? (tags.results?.tags?.counters?.total ?? 0) : 0),
      },
    };
  }, [
    includePeople,
    includeGroups,
    includeTags,
    people.results,
    groups.results,
    tags.results,
  ]);

  const onLoadMore = useCallback(async () => {
    await Promise.all([
      includePeople ? people.onLoadMore() : Promise.resolve(),
      includeGroups ? groups.onLoadMore() : Promise.resolve(),
      includeTags ? tags.onLoadMore() : Promise.resolve(),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    includeGroups,
    includePeople,
    includeTags,
    people.onLoadMore,
    groups.onLoadMore,
    tags.onLoadMore,
  ]);

  const loading =
    (includePeople && people.loading) ||
    (includeGroups && groups.loading) ||
    (includeTags && tags.loading);

  const error = people.error || groups.error || tags.error;

  return {
    loading,
    error,
    results,
    onLoadMore,

    loadingPeople: people.loading,
    loadingTeams: groups.loading,
    loadingTags: tags.loading,

    errorPeople: people.error,
    errorTeams: groups.error,
    errorTags: tags.error,

    resultsPeople: people.results,
    resultsTeams: groups.results,
    resultsTags: tags.results,

    onLoadMorePeople: people.onLoadMore,
    onLoadMoreTeams: groups.onLoadMore,
    onLoadMoreTags: tags.onLoadMore,
  };
};

export default useGlobalSearchParallel;
